import React, {
  forwardRef,
  Ref,
  ReactElement,
  ReactNode,
  useState,
} from 'react';
import {
  Dialog,
  Button,
  Stack,
  Typography,
  Slide,
  useMediaQuery,
  useTheme,
  Card,
  Grid,
  Box,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import {
  mobileCardSx,
  cardSx,
  cardHeaderSx,
} from 'utils';
import { TransitionProps } from '@mui/material/transitions';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { Cancel, JoinFull } from '@mui/icons-material';

interface DuplicateAccountDialogProps {
  open: boolean;
  email: string;
  handleClose: () => void;
  handleAction: () => void;
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>,
) {
  return <Slide direction='left' ref={ref} {...props} />;
});

const DuplicateAccountDialog = ({
  open,
  email,
  handleClose,
  handleAction,
}: DuplicateAccountDialogProps): ReactNode => {
  const { t } = useTranslation();
  const { palette, breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const [sending, setSending] = useState<boolean>(false); 

  return (
    <Dialog
    open={open}
    onClose={handleClose}
    TransitionComponent={Transition}
    aria-label={`${t('labels.resend_registration', { ns: 'userSettings' })} Dialog`}
    fullScreen={mobile}
    fullWidth
    sx={{ mt: { xs: -1, lg: 0 }, pb: 0 }}
  >
    <ValidatorForm
      onSubmit={handleAction}
      instantValidate={false}
    >
      <Card sx={mobile ? mobileCardSx : cardSx}>
        <Box
          sx={
            mobile
              ? {
                  display: 'flex',
                  p: { xs: 2, sm: 4 },
                  backgroundColor: palette.secondary.main,
                }
              : cardHeaderSx
          }
        >
          <Box sx={{ flex: 1, p: 0.5 }}>
            <Typography
              variant={'h4'}
              sx={{
                color: palette.secondary.contrastText,
                textAlign: 'left',
              }}
            >
              {t("registration.emailInUse")}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: 2 }}>
          <Grid container width={'100%'} spacing={1.5}>
            <Grid size={12}>
              <Typography variant='h6'>
                {t("combineAccounts.message", { email: email })}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Stack
                width='100%'
                direction='row'
                spacing={2}
                justifyContent='flex-end'
                mt={2}
                display='flex'
                alignItems='flex-end'
                alignContent='flex-end'
              >
                <Button
                  variant={'outlined'}
                  onClick={handleClose}
                  color={'secondary'}
                  endIcon={<Cancel />}
                  disabled={sending}
                >
                  {t('cancel')}
                </Button>
                <Button
                  variant={'contained'}
                  type='submit'
                  color={'secondary'}
                  endIcon={<JoinFull />}
                  disabled={sending}
                  onClick={(()=> setSending(true))}
                >
                  {t('combine')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Card>
    </ValidatorForm>
  </Dialog>
  );
};

export default DuplicateAccountDialog;
